/* Paleta completa — harta + detaliu + cadrane de familie.
   Structura aleasa de user pe 2026-08-23, dupa testarea a patru prototipuri de interactiune:
   nu mai exista zoom si nici deplasare a benzii, fiindca harta ramane mereu intreaga pe ecran
   si arata in permanenta unde te afli in spectru.
   Hex-urile vin din window.TD_COLOR_MAP (sursa unica a temei), nu din date proprii. */
(() => {
  const MAP = () => window.TD_COLOR_MAP || { by_family: {}, by_family_multi: {}, flat: {} };

  /* Bucket-ul de material afisat in pastile. PLA Matte intra in PLA (cerere explicita
     2026-08-23): pentru cumparator e tot PLA, doar cu finisaj mat. */
  function bucket(material, title) {
    /* Metafield-ul de material spune uneori chiar "Altele" (ex. PET-CF), iar atunci
       titlul produsului e sursa mai buna: "PET-CF" se citeste corect ca PETG. */
    let src = (material || '').toString().trim();
    if (!src || /^(altele|other)$/i.test(src)) src = (title || '').toString().trim();
    const u = src.toUpperCase();
    if (u.startsWith('PLA')) return 'PLA';
    if (u.startsWith('PETG') || u.startsWith('PET-') || u === 'PET') return 'PETG';
    if (u.startsWith('ABS')) return 'ABS';
    if (u.startsWith('ASA')) return 'ASA';
    if (u.startsWith('TPU')) return 'TPU';
    if (u.startsWith('PA')) return 'PA / Nylon';
    if (u.startsWith('PC')) return 'PC';
    return src || 'Altele';
  }

  function hsl(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255,
          g = parseInt(hex.slice(3, 5), 16) / 255,
          b = parseInt(hex.slice(5, 7), 16) / 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    let h = 0;
    if (d) {
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60; if (h < 0) h += 360;
    }
    const l = (mx + mn) / 2;
    return { h: h, s: d ? d / (1 - Math.abs(2 * l - 1)) : 0, l: l };
  }

  /* Ordinea pe harta: intai neutrele (de la alb la negru), apoi spectrul complet. */
  function spectrumSort(a, b) {
    const ca = hsl(a.sortHex), cb = hsl(b.sortHex);
    const na = ca.s < 0.14, nb = cb.s < 0.14;
    if (na !== nb) return na ? -1 : 1;
    if (na) return cb.l - ca.l;
    if (Math.abs(ca.h - cb.h) > 4) return ca.h - cb.h;
    return cb.l - ca.l;
  }

  function esc(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function fill(hexes) {
    if (hexes.length === 1) return hexes[0];
    const stops = hexes.map((h, i) => {
      const from = Math.round(i / hexes.length * 100);
      const to = Math.round((i + 1) / hexes.length * 100);
      return h + ' ' + from + '%, ' + h + ' ' + to + '%';
    }).join(', ');
    return 'linear-gradient(135deg, ' + stops + ')';
  }

  class PaletteExplorer extends HTMLElement {
    connectedCallback() {
      const holder = this.parentElement.querySelector('[data-palette-data]');
      if (!holder) return;
      let families = [];
      try { families = JSON.parse(holder.textContent); } catch (err) { return; }

      this.single = [];
      this.multi = [];
      this.families = [];
      families.forEach((f) => {
        const fam = MAP().by_family[f.h] || {};
        const famMulti = MAP().by_family_multi[f.h] || {};
        const mat = bucket(f.m, f.t);
        const entry = { title: f.t, handle: f.h, url: f.u, mat: mat, colors: [] };
        const seen = {};
        /* [valoare optiune, id varianta, disponibila] -> pastram, pentru fiecare nuanta,
           varianta pe care o deschidem la clic. Daca aceeasi culoare exista la Refill si la
           Bobina, o preferam pe cea in stoc. */
        const variants = {};
        (f.v || []).forEach((row) => {
          const raw = String(row[0] || '');
          const cur = variants[raw];
          if (!cur || (!cur.available && row[2])) variants[raw] = { id: row[1], available: !!row[2] };
        });
        const names = (f.v || []).map((row) => String(row[0] || ''));
        names.forEach((name) => {
          /* Valorile optiunii poarta sufixe care nu tin de culoare: ambalaj "(Refill)",
             duritate "(TPU 90A - TPU 85A)", greutate "(0.5kg)". Tabelul de hex-uri le
             indexeaza fara sufix. */
          const key = String(name).toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').trim();
          if (!key || seen[key]) return;
          seen[key] = true;
          const label = String(name).replace(/\s*\([^)]*\)\s*$/, '').trim() || String(name);
          const multi = famMulti[key];
          const single = fam[key] || MAP().flat[key];
          const hit = variants[name];
          const url = hit ? f.u + '?variant=' + hit.id : f.u;
          const base = { name: label, key: key, family: f.t, handle: f.h, url: url, mat: mat,
                         available: hit ? hit.available : true };
          if (multi && multi.length > 1) {
            const rec = Object.assign({ hexes: multi, sortHex: multi[0] }, base);
            this.multi.push(rec);
            entry.colors.push(rec);
          } else if (single) {
            const rec = Object.assign({ hexes: [single], sortHex: single }, base);
            this.single.push(rec);
            entry.colors.push(rec);
          }
        });
        if (entry.colors.length) this.families.push(entry);
      });

      this.single.sort(spectrumSort);
      this.multi.sort(spectrumSort);
      this.families.forEach((f) => f.colors.sort(spectrumSort));

      this.state = { mat: null, family: null, start: 0, size: PaletteExplorer.DEFAULT_WIN };
      this.els = {
        mats: this.querySelector('[data-mats]'),
        fams: this.querySelector('[data-fams]'),
        map: this.querySelector('[data-map]'),
        win: this.querySelector('[data-win-el]'),
        detail: this.querySelector('[data-detail]'),
        famCards: this.querySelector('[data-famcards]'),
        famNote: this.querySelector('[data-famnote]'),
        winCount: this.querySelector('[data-wincount]'),
        readout: this.querySelector('[data-readout]'),
        multi: this.querySelector('[data-band="multi"]'),
        multiWrap: this.querySelector('[data-multiwrap]'),
        empty: this.querySelector('[data-empty]')
      };
      if (!this.els.map) return;

      this.renderMaterials();
      this.bind();
      this.render();
    }

    bind() {
      this.addEventListener('click', (e) => {
        const m = e.target.closest('[data-mat]');
        if (m) {
          const v = m.dataset.mat;
          this.state.mat = this.state.mat === v ? null : v;
          this.state.family = null;
          this.state.start = 0;
          this.renderMaterials();
          this.render();
          return;
        }
        const f = e.target.closest('[data-family]');
        if (f) {
          const v = f.dataset.family;
          this.state.family = this.state.family === v ? null : v;
          this.state.start = 0;
          this.renderFamilies();
          this.render();
          return;
        }
        const w = e.target.closest('[data-win]');
        if (w) {
          const delta = w.dataset.win === '+' ? 4 : -4;
          this.state.size = Math.max(PaletteExplorer.MIN_WIN,
                            Math.min(PaletteExplorer.MAX_WIN, this.state.size + delta));
          this.render();
        }
      });

      /* Rotita muta fereastra de-a lungul hartii. La capete nu interceptam evenimentul,
         ca sa nu blocam derularea paginii sub cursor. */
      this.els.map.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        const list = this.filtered();
        const maxStart = Math.max(0, list.length - this.state.size);
        if ((e.deltaY < 0 && this.state.start <= 0) || (e.deltaY > 0 && this.state.start >= maxStart)) return;
        e.preventDefault();
        const step = Math.max(1, Math.round(this.state.size / 3));
        this.state.start += e.deltaY > 0 ? step : -step;
        this.render();
      }, { passive: false });

      const jump = (clientX) => {
        const list = this.filtered();
        const r = this.els.map.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
        this.state.start = Math.round(ratio * list.length - this.state.size / 2);
        this.render();
      };
      let dragging = false;
      this.els.map.addEventListener('pointerdown', (e) => {
        dragging = true;
        try { this.els.map.setPointerCapture(e.pointerId); } catch (err) { /* fara captura */ }
        this.els.map.classList.add('is-dragging');
        jump(e.clientX);
      });
      this.els.map.addEventListener('pointermove', (e) => { if (dragging) jump(e.clientX); });
      const stop = () => {
        if (!dragging) return;
        dragging = false;
        this.els.map.classList.remove('is-dragging');
      };
      this.els.map.addEventListener('pointerup', stop);
      this.els.map.addEventListener('pointercancel', stop);
      this.els.map.addEventListener('lostpointercapture', stop);

      this.els.map.addEventListener('keydown', (e) => {
        const step = e.shiftKey ? this.state.size : Math.max(1, Math.round(this.state.size / 3));
        if (e.key === 'ArrowLeft') { this.state.start -= step; e.preventDefault(); this.render(); }
        if (e.key === 'ArrowRight') { this.state.start += step; e.preventDefault(); this.render(); }
      });
    }

    filtered() {
      /* Harta generala ramane dedicata nuantelor simple. Cand utilizatorul alege insa
         o familie formata exclusiv din gradient/multicolor, includem acele nuante in
         harta si detaliu; altfel filtrul exista, dar rezultatul pare gol. */
      const source = this.state.family ? this.single.concat(this.multi) : this.single;
      return source.filter((c) =>
        (!this.state.mat || c.mat === this.state.mat) &&
        (!this.state.family || c.family === this.state.family));
    }

    materials() {
      const all = this.single.concat(this.multi);
      const counts = {};
      all.forEach((c) => { counts[c.mat] = (counts[c.mat] || 0) + 1; });
      return Object.keys(counts).sort((a, b) => counts[b] - counts[a])
        .map((k) => ({ name: k, n: counts[k] }));
    }

    renderMaterials() {
      this.els.mats.innerHTML = this.materials().map((m) => {
        const on = this.state.mat === m.name;
        return '<button type="button" class="td-pal-pill' + (on ? ' is-selected' : '') +
          '" data-mat="' + esc(m.name) + '" aria-pressed="' + on + '">' +
          m.name + '<b>' + m.n + '</b></button>';
      }).join('');
      this.renderFamilies();
    }

    /* Al doilea rand: familiile din materialul ales — la PLA apar toate cele 16. */
    renderFamilies() {
      if (!this.state.mat) {
        this.els.fams.hidden = true;
        this.els.fams.innerHTML = '';
        return;
      }
      const list = this.single.concat(this.multi).filter((c) => c.mat === this.state.mat);
      const byFam = {};
      list.forEach((c) => { byFam[c.family] = (byFam[c.family] || 0) + 1; });
      const names = Object.keys(byFam).sort((a, b) => byFam[b] - byFam[a]);
      this.els.fams.hidden = false;
      this.els.fams.innerHTML = names.map((n) => {
        const on = this.state.family === n;
        return '<button type="button" class="td-pal-pill td-pal-pill--sub' + (on ? ' is-selected' : '') +
          '" data-family="' + esc(n) + '" aria-pressed="' + on + '">' +
          n + '<b>' + byFam[n] + '</b></button>';
      }).join('');
    }

    render() {
      const list = this.filtered();
      const size = Math.min(this.state.size, Math.max(1, list.length));
      this.state.start = Math.max(0, Math.min(Math.max(0, list.length - size), this.state.start));
      const win = list.slice(this.state.start, this.state.start + size);

      this.renderMap(list, size);
      this.renderDetail(win);
      this.renderFamilyCards(win);
      this.renderMulti();

      this.els.winCount.textContent = size + ' nuanțe';
      this.els.empty.hidden = (list.length + this.multi.length) > 0;
      if (this.els.readout) {
        this.els.readout.textContent = win.length
          ? win[0].name + ' → ' + win[win.length - 1].name
          : '';
      }
      this.els.map.setAttribute('aria-valuemax', String(Math.max(0, list.length - size)));
      this.els.map.setAttribute('aria-valuenow', String(this.state.start));
    }

    renderMap(list, size) {
      const key = list.length + '|' + (this.state.mat || '') + '|' + (this.state.family || '');
      if (this._mapKey !== key) {
        this._mapKey = key;
        this.els.map.querySelectorAll('.td-pal-map__cell').forEach((el) => el.remove());
        const cells = list.map((c) =>
          '<span class="td-pal-map__cell" style="background:' + fill(c.hexes) +
          '" title="' + esc(c.name + ' — ' + c.family) + '"></span>'
        ).join('');
        this.els.win.insertAdjacentHTML('beforebegin', cells);
      }
      const pct = 100 / Math.max(1, list.length);
      this.els.win.style.left = (this.state.start * pct) + '%';
      this.els.win.style.width = (size * pct) + '%';
    }

    renderDetail(win) {
      this.els.detail.innerHTML = win.map((c) =>
        '<a class="td-pal-chip" href="' + c.url + '" style="--sw:' + fill(c.hexes) + '"' +
        ' title="' + esc(c.name + ' — ' + c.family + ' · ' + c.hexes.join(' / ')) + '">' +
        '<span class="td-pal-chip__fill"></span>' +
        '<span class="td-pal-chip__label"><b>' + esc(c.family) + '</b>' +
        '<span>' + esc(c.name) + '</span><u>' + c.hexes.join(' / ') + '</u></span></a>'
      ).join('');
    }

    /* Cadranele: familiile care au nuante in fereastra, fiecare cu gama ei COMPLETA,
       cu nuantele din fereastra evidentiate. Asa vezi si ce altceva mai are familia
       in jurul culorii care te intereseaza. */
    renderFamilyCards(win) {
      const inWindow = {};
      win.forEach((c) => {
        if (!inWindow[c.handle]) inWindow[c.handle] = {};
        inWindow[c.handle][c.key] = true;
      });
      const cards = this.families
        .filter((f) => inWindow[f.handle])
        .map((f) => ({ f: f, n: Object.keys(inWindow[f.handle]).length }))
        .sort((a, b) => b.n - a.n || a.f.title.localeCompare(b.f.title));

      /* Cardul nu mai e un singur link: fiecare bulina duce la nuanta ei exacta
         (`?variant=`), iar titlul la familie. Inainte, un clic pe bulina te lasa pe
         pagina familiei si trebuia sa cauti culoarea din nou. */
      this.els.famCards.innerHTML = cards.map((item) => {
        const f = item.f;
        const dots = f.colors.map((c) =>
          '<a class="td-pal-dot' + (inWindow[f.handle][c.key] ? ' is-match' : '') +
          '" href="' + c.url + '" style="background:' + fill(c.hexes) +
          '" title="' + esc(c.name + ' · ' + c.hexes.join(' / ')) +
          '" aria-label="' + esc(c.name + ' — ' + f.title) + '"></a>'
        ).join('');
        return '<div class="td-pal-famcard">' +
          '<a class="td-pal-famcard__title" href="' + f.url + '">' + esc(f.title) + '</a>' +
          '<p>' + esc(f.mat.toUpperCase()) + ' · ' + item.n + ' ÎN ACEASTĂ GAMĂ</p>' +
          '<div class="td-pal-famcard__dots">' + dots + '</div></div>';
      }).join('');

      if (this.els.famNote) {
        this.els.famNote.textContent = cards.length
          ? cards.length + (cards.length === 1 ? ' filament' : ' filamente') + ' · nuanțele potrivite sunt încercuite'
          : '';
      }
    }

    renderMulti() {
      if (this._multiDone) return;
      this._multiDone = true;
      this.els.multi.innerHTML = this.multi.map((c) =>
        '<a class="td-pal-chip td-pal-chip--multi" href="' + c.url + '" style="--sw:' + fill(c.hexes) + '"' +
        ' title="' + esc(c.name + ' — ' + c.family + ' · ' + c.hexes.join(' / ')) + '">' +
        '<span class="td-pal-chip__fill"></span>' +
        '<span class="td-pal-chip__label"><b>' + esc(c.family) + '</b>' +
        '<span>' + esc(c.name) + '</span></span></a>'
      ).join('');
      this.els.multiWrap.hidden = this.multi.length === 0;
    }
  }

  PaletteExplorer.MIN_WIN = 4;
  PaletteExplorer.MAX_WIN = 40;
  PaletteExplorer.DEFAULT_WIN = 12;

  /* Banda decorativa folosita in bara de sub header si pe cardul de pe homepage.
     Isi ia culorile din aceeasi harta ca restul temei si esantioneaza uniform pe spectru,
     ca sa arate tot intervalul, nu primele N nuante. */
  class ColorStrip extends HTMLElement {
    connectedCallback() {
      const map = MAP().by_family || {};
      const all = [];
      Object.keys(map).forEach((slug) => {
        Object.keys(map[slug]).forEach((name) => all.push(map[slug][name]));
      });
      const uniq = [];
      const seen = {};
      all.forEach((hex) => {
        const k = String(hex).toUpperCase();
        if (!seen[k]) { seen[k] = true; uniq.push(k); }
      });
      if (!uniq.length) return;
      uniq.sort((a, b) => spectrumSort({ sortHex: a }, { sortHex: b }));

      const want = Math.max(6, Number(this.dataset.count) || 40);
      const step = uniq.length / Math.min(want, uniq.length);
      const picked = [];
      for (let i = 0; i < Math.min(want, uniq.length); i++) {
        picked.push(uniq[Math.floor(i * step)]);
      }
      this.innerHTML = picked.map((hex) => '<i style="background:' + hex + '"></i>').join('');
    }
  }
  if (!customElements.get('color-strip')) {
    customElements.define('color-strip', ColorStrip);
  }

  /* Ajutoarele de mai sus sunt singura implementare din tema pentru bucket-ul de material,
     ordinea pe spectru si umplerea multicolora. Le expunem ca sa le poata refolosi si alte
     selectoare (ex. `td-palette-picker.js`, de pe varianta alternativa de Home) fara sa le
     copieze. Nimic din comportamentul paginii /pages/paleta nu se schimba. */
  window.TD_PALETTE_UTILS = { bucket: bucket, hsl: hsl, spectrumSort: spectrumSort, esc: esc, fill: fill, map: MAP };

  if (!customElements.get('palette-explorer')) {
    customElements.define('palette-explorer', PaletteExplorer);
  }
})();
